import type { CartesianPose } from '@sb/geometry';
import { applyCompoundPose, ZERO_POSE } from '@sb/geometry';
import type { Quaternion } from '@sb/geometry/Quaternion';
import { quaternionMultiply } from '@sb/geometry/Quaternion';
import type { EquipmentItem } from '@sb/integrations/types';
import { isEndEffector } from '@sb/integrations/utils';
import type { TCPOffsetOption } from '@sb/motion-planning';
import { getTCPIndexFromTCPOption } from '@sb/motion-planning/TCPOffsetOption';

/**
 * Returns the quaternion (i,j,k,w) orientation of the selected ROS tooltip link
 * relative to tool0. If no end-effector is enabled or link is 'tool0', returns identity.
 */
export function getTCPOrientationFromTool0ByIndex(
  equipmentItems: EquipmentItem[],
  tcpIndex: number,
): Quaternion {
  if (tcpIndex < 0) {
    return { i: 0, j: 0, k: 0, w: 1 };
  }

  // Find the current enabled end-effector configuration
  const item = equipmentItems.find(
    (e) => e.isEnabled && isEndEffector(e.config.kind),
  );

  if (!item) return { i: 0, j: 0, k: 0, w: 1 };

  // Grab tcpTransform from either tooltips[index], device-specific fields, or a single tcpTransform
  const itemConfig: any = item.config as any;

  let tcpQuaternion: Quaternion | undefined;

  if (
    Array.isArray(itemConfig.tooltips) &&
    itemConfig.tooltips[tcpIndex]?.tcpTransform
  ) {
    // Custom Gripper
    tcpQuaternion = itemConfig.tooltips[tcpIndex].tcpTransform;
  } else if (
    itemConfig.kind === 'OnRobotDualQuickChanger' &&
    itemConfig.grippers
  ) {
    if (tcpIndex === 0 && itemConfig.grippers.primary?.tcpTransform)
      tcpQuaternion = itemConfig.grippers.primary.tcpTransform;
    else if (tcpIndex === 1 && itemConfig.grippers.secondary?.tcpTransform)
      tcpQuaternion = itemConfig.grippers.secondary.tcpTransform;
  } else if (itemConfig.tcpTransform && tcpIndex === 0) {
    // Typical gripper
    tcpQuaternion = itemConfig.tcpTransform;
  }

  // Find the enabled mounting bracket, if any
  const mountingBracket = equipmentItems.find(
    (e) => e.isEnabled && e.config.kind === 'MountingBracket',
  );

  // If there's a mounting bracket, compose it with the gripper rotation
  let mountingBracketQuaternion: Quaternion | undefined;

  if (
    mountingBracket?.config.kind === 'MountingBracket' &&
    mountingBracket.config.tcpTransform
  ) {
    mountingBracketQuaternion = mountingBracket.config.tcpTransform;
  }

  // Compose quaternions if we have a mounting bracket
  const tcpQuat = tcpQuaternion ?? { i: 0, j: 0, k: 0, w: 1 };

  const combinedQuaternion = mountingBracketQuaternion
    ? quaternionMultiply(mountingBracketQuaternion, tcpQuat)
    : tcpQuat;

  return combinedQuaternion;
}

/**
 * Resolve the ROS tooltip link name and the corresponding tool0->tip quaternion
 * in a single call, given the tcpOption and current equipment items.
 */
export function getTCPLinkAndQuaternion(
  equipmentItems: EquipmentItem[],
  tcpOption: TCPOffsetOption,
): {
  tcpLink: string;
  tcpOrientationFromTool0: Quaternion;
  tcpIndex: number;
} {
  const tcpIndex = getTCPIndexFromTCPOption(tcpOption);

  let tcpLink: string;

  if (tcpIndex < 0) {
    tcpLink = 'tool0';
  } else {
    const endEffectorItem = equipmentItems.find(
      (e) => e.isEnabled && isEndEffector(e.config.kind),
    );

    // No enabled end-effector: degrade to tool0 so callers (e.g. jog) hand a
    // valid frame to the motion planner, which then surfaces a user-facing
    // motion-planning failure instead of an unhandled exception bubbling out
    // of adHocMotionMutex.runExclusive (RoutineRunner.ts:1913).
    tcpLink = endEffectorItem
      ? `${endEffectorItem.id}_tcp_${tcpIndex}`
      : 'tool0';
  }

  const tcpOrientationFromTool0 = getTCPOrientationFromTool0ByIndex(
    equipmentItems,
    tcpIndex,
  );

  return { tcpLink, tcpOrientationFromTool0, tcpIndex };
}

/**
 * Returns the full pose (translation + rotation) of the selected TCP relative
 * to tool0/flange. Mirrors getTCPOrientationFromTool0ByIndex but keeps the
 * translation. Returns ZERO_POSE (identity) for tool0 (tcpIndex < 0) or when no
 * enabled end-effector is found.
 */
export function getTCPTransformFromTool0(
  equipmentItems: EquipmentItem[],
  tcpIndex: number,
): CartesianPose {
  if (tcpIndex < 0) return ZERO_POSE;

  const item = equipmentItems.find(
    (e) => e.isEnabled && isEndEffector(e.config.kind),
  );

  if (!item) return ZERO_POSE;

  const itemConfig: any = item.config as any;

  let tcpPose: CartesianPose | undefined;

  if (
    Array.isArray(itemConfig.tooltips) &&
    itemConfig.tooltips[tcpIndex]?.tcpTransform
  ) {
    tcpPose = itemConfig.tooltips[tcpIndex].tcpTransform;
  } else if (
    itemConfig.kind === 'OnRobotDualQuickChanger' &&
    itemConfig.grippers
  ) {
    if (tcpIndex === 0 && itemConfig.grippers.primary?.tcpTransform)
      tcpPose = itemConfig.grippers.primary.tcpTransform;
    else if (tcpIndex === 1 && itemConfig.grippers.secondary?.tcpTransform)
      tcpPose = itemConfig.grippers.secondary.tcpTransform;
  } else if (itemConfig.tcpTransform && tcpIndex === 0) {
    tcpPose = itemConfig.tcpTransform;
  }

  const tcp = tcpPose ?? ZERO_POSE;

  const mountingBracket = equipmentItems.find(
    (e) => e.isEnabled && e.config.kind === 'MountingBracket',
  );

  const bracketPose =
    mountingBracket?.config.kind === 'MountingBracket' &&
    mountingBracket.config.tcpTransform
      ? (mountingBracket.config.tcpTransform as CartesianPose)
      : undefined;

  // Compose the bracket (flange->bracket) with the gripper TCP (bracket->tcp)
  // so the result is the full flange->tcp pose: applyCompoundPose(child_in_parent,
  // parent_in_grandparent) = child_in_grandparent.
  return bracketPose ? applyCompoundPose(tcp, bracketPose) : tcp;
}
